import React, { useState } from "react";
import { FaTrashAlt, FaPaperPlane, FaUser, FaClock } from "react-icons/fa";
import { convertTimestampToDate } from "../../../../utils";
import { OvalLoader } from "../../../Common/Loaders";
import { useAddAdminResponseMutation } from "../../../../services/baseQuery";

export default function ProjectQuestions({ project, openDeleteModal }) {
  const [adminAnswers, setAdminAnswers] = useState({});
  const [showAdminResponse, setShowAdminResponse] = useState({});

  const [addAdminResponse, { isLoading: isSubmitting }] =
    useAddAdminResponseMutation();

  const handleAdminAnswerChange = (questionId, value) => {
    setAdminAnswers({
      ...adminAnswers,
      [questionId]: value,
    });
  };

  const handleSubmitAdminAnswer = async questionId => {
    if (!adminAnswers[questionId] || adminAnswers[questionId].trim() === "")
      return;

    try {
      await addAdminResponse({
        projectId: project.id,
        conversationId: questionId,
        adminResponse: adminAnswers[questionId].trim(),
      }).unwrap();

      // Clear the input and hide the response section
      setAdminAnswers({
        ...adminAnswers,
        [questionId]: "",
      });
      setShowAdminResponse({
        ...showAdminResponse,
        [questionId]: false,
      });
    } catch (error) {
      console.error("Error adding admin response:", error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <div className="questionsContainer">
      {project.conversations && project.conversations.length > 0 ? (
        project.conversations.map(conversation => {
          const questionId =
            conversation.merchantId + conversation.askedOn.seconds;
          return (
            <div className="questionCard" key={questionId}>
              <div className="questionHeader">
                <div className="userInfo">
                  <div className="avatarContainer">
                    {conversation.merchantData.displayImage ? (
                      <img
                        src={conversation.merchantData.displayImage}
                        alt="merchant"
                        className="userAvatar"
                      />
                    ) : (
                      <div className="defaultAvatar">
                        <FaUser />
                      </div>
                    )}
                  </div>
                  <div className="userDetails">
                    <div className="userName">
                      {conversation.merchantData.displayName}
                    </div>
                    <div className="timestamp">
                      <FaClock /> {convertTimestampToDate(conversation.askedOn)}
                    </div>
                  </div>
                </div>
                <button
                  className="deleteButton"
                  onClick={() => openDeleteModal(questionId, "question")}
                >
                  <FaTrashAlt />
                </button>
              </div>

              <div className="questionContent">
                <div className="questionText">{conversation.questionText}</div>
              </div>

              {/* Customer Response Section */}
              {conversation.response && (
                <div className="responseSection customerResponse">
                  <div className="responseHeader">
                    <div className="responseLabel">Customer Response</div>
                    <div className="timestamp">
                      <FaClock />
                      {convertTimestampToDate(conversation.responsedOn)}
                    </div>
                  </div>
                  <div className="responseContent">
                    <div className="responseText">
                      {conversation.response.text}
                    </div>
                    {conversation.response.images &&
                      conversation.response.images.length > 0 && (
                        <div className="imageGrid">
                          {conversation.response.images.map((image, index) => (
                            <div className="imageWrapper" key={index}>
                              <img
                                src={image}
                                alt="Customer response"
                                className="responseImage"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Admin Response Section */}
              {conversation.adminResponse ? (
                <div className="responseSection adminResponse">
                  <div className="responseHeader">
                    <div className="responseLabel">Admin Response</div>
                    <div className="timestamp">
                      <FaClock />
                      {convertTimestampToDate(
                        conversation.adminResponse.answeredOn
                      )}
                    </div>
                  </div>
                  <div className="responseContent">
                    <div className="responseText">
                      {conversation.adminResponse.text}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!showAdminResponse[questionId] ? (
                    <button
                      className="addResponseButton"
                      onClick={() =>
                        setShowAdminResponse({
                          ...showAdminResponse,
                          [questionId]: true,
                        })
                      }
                    >
                      <FaPaperPlane /> Add Response
                    </button>
                  ) : (
                    <div className="responseInputSection">
                      <textarea
                        className="responseTextarea"
                        placeholder="Type your response here..."
                        value={adminAnswers[questionId] || ""}
                        onChange={e =>
                          handleAdminAnswerChange(questionId, e.target.value)
                        }
                      />
                      <div className="responseActions">
                        <button
                          className="cancelButton"
                          onClick={() => {
                            setShowAdminResponse({
                              ...showAdminResponse,
                              [questionId]: false,
                            });
                            setAdminAnswers({
                              ...adminAnswers,
                              [questionId]: "",
                            });
                          }}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          className="submitButton"
                          onClick={() => handleSubmitAdminAnswer(questionId)}
                          disabled={
                            isSubmitting ||
                            !adminAnswers[questionId] ||
                            adminAnswers[questionId].trim() === ""
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <div className="submitLoader">
                                <OvalLoader color="#fff" />
                              </div>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane /> Submit
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })
      ) : (
        <div className="emptyState">
          <div className="emptyStateContent">
            <div className="emptyStateIcon">
              <FaUser />
            </div>
            <h3>No Questions Yet</h3>
            <p>There are no questions asked for this project yet.</p>
          </div>
        </div>
      )}

      <style jsx>{`
        .questionsContainer {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .questionCard {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          border: 1px solid #f0f0f0;
          transition: all 0.2s ease;
        }

        .questionCard:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border-color: #e6e6e6;
        }

        .questionHeader {
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: linear-gradient(to right, #fafafa, #ffffff);
          border-bottom: 1px solid #f0f0f0;
        }

        .userInfo {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .avatarContainer {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          background: linear-gradient(45deg, #f5f5f5, #fafafa);
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .userAvatar {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .defaultAvatar {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #bfbfbf;
          font-size: 20px;
          background: linear-gradient(45deg, #f5f5f5, #fafafa);
        }

        .userDetails {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .userName {
          font-weight: 600;
          color: #262626;
          font-size: 15px;
        }

        .timestamp {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #8c8c8c;
          font-size: 13px;
        }

        .timestamp svg {
          font-size: 12px;
        }

        .questionContent {
          padding: 20px;
          background: #ffffff;
        }

        .questionText {
          color: #262626;
          font-size: 15px;
          line-height: 1.6;
        }

        .responseSection {
          position: relative;
          padding: 20px;
          background: #ffffff;
          border-top: 1px solid #f0f0f0;
        }

        .responseSection::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
        }

        .responseSection.customerResponse::before {
          background: linear-gradient(to bottom, #52c41a, #95de64);
        }

        .responseSection.adminResponse::before {
          background: linear-gradient(to bottom, #1890ff, #69c0ff);
        }

        .responseHeader {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .responseLabel {
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .customerResponse .responseLabel {
          color: #52c41a;
        }

        .adminResponse .responseLabel {
          color: #1890ff;
        }

        .responseContent {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .responseText {
          color: #262626;
          font-size: 14px;
          line-height: 1.6;
        }

        .imageGrid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
          margin-top: 12px;
        }

        .imageWrapper {
          position: relative;
          padding-top: 100%;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .responseImage {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .imageWrapper:hover .responseImage {
          transform: scale(1.05);
        }

        .addResponseButton {
          margin: 20px;
          padding: 10px 20px;
          background: linear-gradient(45deg, #1890ff, #40a9ff);
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-size: 14px;
          box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
        }

        .addResponseButton:hover {
          background: linear-gradient(45deg, #40a9ff, #69c0ff);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(24, 144, 255, 0.3);
        }

        .responseInputSection {
          padding: 20px;
          background: #fafafa;
          border-top: 1px solid #f0f0f0;
          width: 100%;
          box-sizing: border-box;
        }

        .responseTextarea {
          width: 100%;
          min-height: 120px;
          padding: 12px;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          resize: vertical;
          font-family: inherit;
          font-size: 14px;
          line-height: 1.6;
          background: #ffffff;
          box-sizing: border-box;
          margin: 0;
          max-width: 100%;
        }

        .responseTextarea:focus {
          outline: none;
          border-color: #40a9ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
        }

        .responseActions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 16px;
          width: 100%;
        }

        .cancelButton,
        .submitButton,
        .deleteButton {
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          font-size: 14px;
          border: none;
        }

        .cancelButton {
          background: #f5f5f5;
          color: #595959;
        }

        .cancelButton:hover {
          background: #e8e8e8;
        }

        .submitButton {
          background: linear-gradient(45deg, #1890ff, #40a9ff);
          color: white;
          box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
        }

        .submitButton:hover:not(:disabled) {
          background: linear-gradient(45deg, #40a9ff, #69c0ff);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(24, 144, 255, 0.3);
        }

        .submitButton:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .deleteButton {
          padding: 8px;
          background: transparent;
          color: #ff4d4f;
          opacity: 0.7;
          transition: all 0.2s ease;
        }

        .deleteButton:hover {
          opacity: 1;
          background: #fff1f0;
          color: #ff7875;
        }

        .submitLoader {
          width: 16px;
          height: 16px;
        }

        .emptyState {
          padding: 48px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid #f0f0f0;
        }

        .emptyStateContent {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 32px;
          background: linear-gradient(to bottom, #fafafa, #ffffff);
          border-radius: 8px;
          border: 1px dashed #d9d9d9;
        }

        .emptyStateIcon {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(45deg, #f5f5f5, #fafafa);
          border-radius: 50%;
          margin-bottom: 16px;
          color: #bfbfbf;
          font-size: 24px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .emptyStateContent h3 {
          margin: 0 0 8px;
          color: #262626;
          font-size: 18px;
          font-weight: 600;
        }

        .emptyStateContent p {
          margin: 0;
          color: #8c8c8c;
          font-size: 14px;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
